import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { hasPermission, PERMISSIONS, handleAPIError } from '../authUtils';
import { ErrorMessage } from './ErrorMessage';

const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
const ALL_US_STATES = ['AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'];

// Helper functions for date calculations (copied from Disclosure.js)
function toDateStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function getTodayLocal() {
  return toDateStr(new Date());
}

function getUSBankHolidays(year) {
  const h = new Set();
  function obs(d) {
    const day = d.getDay();
    if (day === 0) d.setDate(d.getDate() + 1);
    else if (day === 6) d.setDate(d.getDate() - 1);
    h.add(toDateStr(d));
  }
  function getNthWeekday(year, month, weekday, n) {
    const d = new Date(year, month, 1);
    let count = 0;
    while (true) {
      if (d.getDay() === weekday && ++count === n) return toDateStr(d);
      d.setDate(d.getDate() + 1);
    }
  }
  function getLastWeekday(year, month, weekday) {
    const d = new Date(year, month + 1, 0);
    while (d.getDay() !== weekday) d.setDate(d.getDate() - 1);
    return toDateStr(d);
  }
  obs(new Date(year, 0, 1));
  h.add(getNthWeekday(year, 0, 1, 3));
  h.add(getNthWeekday(year, 1, 1, 3));
  h.add(getLastWeekday(year, 4, 1));
  obs(new Date(year, 5, 19));
  obs(new Date(year, 6, 4));
  h.add(getNthWeekday(year, 8, 1, 1));
  h.add(getNthWeekday(year, 9, 1, 2));
  obs(new Date(year, 10, 11));
  h.add(getNthWeekday(year, 10, 4, 4));
  obs(new Date(year, 11, 25));
  return h;
}

const _holidayCache = {};
function isBizDay(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  const day = d.getDay();
  if (day === 0 || day === 6) return false;
  const year = d.getFullYear();
  if (!_holidayCache[year]) _holidayCache[year] = getUSBankHolidays(year);
  return !_holidayCache[year].has(dateStr);
}

function advanceDays(dateStr, days) {
  const d = new Date(dateStr + 'T12:00:00');
  d.setDate(d.getDate() + days);
  return toDateStr(d);
}

function getFirstPaymentDate(disbursementDateStr, frequency) {
  if (!disbursementDateStr) return '';
  let candidate = advanceDays(disbursementDateStr, frequency === 'daily' ? 1 : 7);
  while (!isBizDay(candidate)) candidate = advanceDays(candidate, 1);
  return candidate;
}

function getPreviousFourMonths() {
  const months = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"];
  const now = new Date();
  const result = [];
  for (let i = 4; i >= 1; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    result.push(months[d.getMonth()]);
  }
  return result;
}

// Currency Input component (from Disclosure.js)
function CurrencyInput({ name, value, onChange, placeholder, disabled }) {
  const [displayValue, setDisplayValue] = useState("");
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (!isFocused && value !== "") {
      const num = parseFloat(value);
      if (!isNaN(num)) {
        setDisplayValue(num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
      }
    }
  }, [value, isFocused]);

  function handleFocus() {
    setIsFocused(true);
    setDisplayValue(value);
  }

  function handleBlur() {
    setIsFocused(false);
    const num = parseFloat(value);
    if (!isNaN(num)) {
      setDisplayValue(num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
    }
  }

  function handleChange(e) {
    const raw = e.target.value.replace(/[^0-9.]/g, "");
    setDisplayValue(raw);
    onChange({ target: { name, value: raw } });
  }

  return (
    <div style={{ position: "relative" }}>
      <span style={{
        position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)",
        color: "#6b7280", fontSize: "14px", pointerEvents: "none"
      }}>$</span>
      <input
        style={{
          width: "100%", border: "1px solid #d1d5db", borderRadius: "6px",
          padding: "8px 10px 8px 22px", fontSize: "14px", boxSizing: "border-box",
          outline: "none", backgroundColor: disabled ? "#f9fafb" : "white",
          color: disabled ? "#9ca3af" : "#111827"
        }}
        name={name}
        value={displayValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
      />
    </div>
  );
}

export default function DealCreate() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = localStorage.getItem('token');
  const queryMerchantId = searchParams.get('merchant_id');

  // Merchant selection/creation state
  const [selectedMerchantId, setSelectedMerchantId] = useState(queryMerchantId ? parseInt(queryMerchantId) : null);
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [selectedMerchantAddress, setSelectedMerchantAddress] = useState(null);
  const [merchantSearch, setMerchantSearch] = useState('');
  const [merchantOptions, setMerchantOptions] = useState([]);
  const [showMerchantDropdown, setShowMerchantDropdown] = useState(false);
  const [showMerchantCreateModal, setShowMerchantCreateModal] = useState(false);

  // Funding entities
  const [fundingEntities, setFundingEntities] = useState([]);
  const [selectedFundingEntityId, setSelectedFundingEntityId] = useState(null);
  const [selectedFundingEntity, setSelectedFundingEntity] = useState(null);

  // Form data
  const [formData, setFormData] = useState({
    state: 'NY',
    purchase_price: '',
    factor_rate: '',
    revenue_purchased: '',
    origination_fee: '',
    broker_fee_merchant: '',
    broker_fee_funder: '',
    payment_frequency: 'daily',
    num_payment_weeks: '',
    disbursement_date: getTodayLocal(),
    first_payment_date: getFirstPaymentDate(getTodayLocal(), 'daily'),
    monthly_revenue_1: '',
    monthly_revenue_2: '',
    monthly_revenue_3: '',
    monthly_revenue_4: '',
    has_refi: false,
    refi_amount: '',
    has_third_party: false,
    third_party_payment: '',
    third_party_name: '',
    is_ny_refi: false,
    ny_prior_finance_charge: '',
    ny_prior_percent_paid: '',
  });

  // Merchant creation form
  const [merchantCreateForm, setMerchantCreateForm] = useState({
    legal_name: '',
    dba_name: '',
    ein: '',
    entity_type: 'LLC',
  });

  const [loading, setLoading] = useState(false);
  // Phase 4a: Change error to object for better error handling
  const [error, setError] = useState(null);

  const monthNames = getPreviousFourMonths();

  // Calculate payment days
  const paymentDays = (() => {
    const weeks = parseInt(formData.num_payment_weeks);
    if (!weeks || isNaN(weeks)) return null;
    return formData.payment_frequency === 'daily' ? weeks * 5 : weeks;
  })();

  const fetchFundingEntities = useCallback(async () => {
    try {
      console.log('[DealCreate] Fetching funding entities from:', `${API_URL}/funding-entities`);
      const response = await axios.get(`${API_URL}/funding-entities`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      console.log('[DealCreate] Funding entities response:', response.data);
      setFundingEntities(response.data);
    } catch (err) {
      console.error('[DealCreate] Failed to load funding entities:', err);
      setError(`Failed to load funding entities: ${err.message}`);
    }
  }, [token]);

  const resolveStateFromMerchant = useCallback(async (merchant, merchantId) => {
    // Helper function to resolve state from merchant data
    // Priority: 1) primary address state, 2) formation state, 3) default 'NY'
    try {
      const addressResponse = await axios.get(`${API_URL}/merchants/${merchantId}/addresses`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const primaryAddress = addressResponse.data.find(a => a.is_primary) || addressResponse.data[0];

      let stateToSet = null;
      if (primaryAddress && primaryAddress.state && ALL_US_STATES.includes(primaryAddress.state)) {
        stateToSet = primaryAddress.state;
      } else if (merchant.state_of_formation && ALL_US_STATES.includes(merchant.state_of_formation)) {
        stateToSet = merchant.state_of_formation;
      }

      if (stateToSet) {
        setFormData(prev => ({
          ...prev,
          state: stateToSet
        }));
      }

      if (primaryAddress) {
        setSelectedMerchantAddress(primaryAddress);
      }
    } catch (err) {
      console.error('Failed to resolve merchant state:', err);
    }
  }, [token]);

  const fetchMerchantDetail = useCallback(async (id) => {
    try {
      const response = await axios.get(`${API_URL}/merchants/${id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setSelectedMerchant(response.data);
      // Resolve state from merchant data (addresses + formation state)
      resolveStateFromMerchant(response.data, id);
    } catch (err) {
      console.error('Failed to load merchant', err);
    }
  }, [token, resolveStateFromMerchant]);

  useEffect(() => {
    if (queryMerchantId) {
      fetchMerchantDetail(parseInt(queryMerchantId));
    }
    fetchFundingEntities();
  }, [queryMerchantId, fetchMerchantDetail, fetchFundingEntities]);

  // Auto-update first_payment_date when disbursement_date or payment_frequency changes
  useEffect(() => {
    if (formData.disbursement_date) {
      setFormData(prev => ({
        ...prev,
        first_payment_date: getFirstPaymentDate(formData.disbursement_date, formData.payment_frequency)
      }));
    }
  }, [formData.disbursement_date, formData.payment_frequency]);

  // Auto-calculate revenue_purchased from factor_rate
  useEffect(() => {
    const pp = parseFloat(formData.purchase_price);
    const fr = parseFloat(formData.factor_rate);
    if (!isNaN(pp) && !isNaN(fr) && pp > 0 && fr > 0) {
      const rp = (pp * fr).toFixed(2);
      setFormData(prev => ({ ...prev, revenue_purchased: rp }));
    }
  }, [formData.purchase_price, formData.factor_rate]);

  // DON'T auto-select funding entity - let user choose explicitly
  // (Backend returns entities sorted by legal_name, so first one is not always the intended one)

  const handleMerchantSearch = async (value) => {
    console.log('[DealCreate] Merchant search for:', value);
    setMerchantSearch(value);
    if (value.length > 0) {
      try {
        const response = await axios.get(`${API_URL}/merchants?limit=100`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        console.log('[DealCreate] Merchants fetched:', response.data.length);
        const filtered = response.data.filter(m =>
          m.legal_name.toLowerCase().includes(value.toLowerCase()) ||
          (m.ein || '').includes(value)
        );
        console.log('[DealCreate] Filtered merchants:', filtered.length);
        setMerchantOptions(filtered);
        setShowMerchantDropdown(true);
      } catch (err) {
        console.error('[DealCreate] Failed to search merchants:', err);
        setError(`Failed to search merchants: ${err.message}`);
      }
    } else {
      setMerchantOptions([]);
      setShowMerchantDropdown(false);
    }
  };

  const fetchMerchantAddresses = async (merchantId) => {
    try {
      const response = await axios.get(`${API_URL}/merchants/${merchantId}/addresses`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      // Find primary address or use first address
      const primaryAddress = response.data.find(a => a.is_primary) || response.data[0];
      if (primaryAddress) {
        setSelectedMerchantAddress(primaryAddress);
        return primaryAddress;
      }
    } catch (err) {
      console.error('Failed to fetch merchant addresses', err);
    }
    return null;
  };

  const selectMerchant = async (merchant) => {
    setSelectedMerchantId(merchant.id);
    setSelectedMerchant(merchant);
    setMerchantSearch('');
    setShowMerchantDropdown(false);

    // Fetch merchant addresses
    const primaryAddress = await fetchMerchantAddresses(merchant.id);

    // Auto-set state: resolve in priority order
    // 1. Prefer merchant's primary business address state (where they operate)
    // 2. Fall back to state_of_formation (where they're incorporated)
    // 3. If neither exists, leave as default 'NY'
    let stateToSet = null;
    if (primaryAddress && primaryAddress.state && ALL_US_STATES.includes(primaryAddress.state)) {
      stateToSet = primaryAddress.state;
    } else if (merchant.state_of_formation && ALL_US_STATES.includes(merchant.state_of_formation)) {
      stateToSet = merchant.state_of_formation;
    }

    if (stateToSet) {
      setFormData(prev => ({
        ...prev,
        state: stateToSet
      }));
    }
  };

  const handleCreateMerchant = async (e) => {
    e.preventDefault();
    if (!merchantCreateForm.legal_name || !merchantCreateForm.ein) {
      alert('Legal name and EIN are required');
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/merchants`, merchantCreateForm, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      // Auto-select the created merchant
      selectMerchant(response.data);
      setShowMerchantCreateModal(false);
      setMerchantCreateForm({ legal_name: '', dba_name: '', ein: '', entity_type: 'LLC' });
    } catch (err) {
      alert('Failed to create merchant: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleMerchantCreateChange = (e) => {
    const { name, value } = e.target;
    setMerchantCreateForm(prev => ({ ...prev, [name]: value }));
  };

  const handleFundingEntityChange = (e) => {
    const entityId = e.target.value ? parseInt(e.target.value) : null;
    setSelectedFundingEntityId(entityId);
    const entity = fundingEntities.find(f => f.id === entityId);
    setSelectedFundingEntity(entity || null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Phase 4a: Check permission before attempting to create
    if (!hasPermission(PERMISSIONS.DEAL_CREATE)) {
      setError({
        response: {
          status: 403,
          data: { detail: 'You do not have permission to create deals.' }
        }
      });
      return;
    }

    if (!selectedMerchantId) {
      setError('Please select or create a merchant');
      return;
    }

    if (!selectedFundingEntityId) {
      setError('Please select a funding entity');
      return;
    }

    if (!formData.state) {
      setError('Please select a state');
      return;
    }

    if (!formData.purchase_price) {
      setError('Purchase price is required');
      return;
    }

    if (!formData.revenue_purchased) {
      setError('Revenue purchased is required');
      return;
    }

    if (!formData.num_payment_weeks) {
      setError('Number of payment weeks is required');
      return;
    }

    setLoading(true);

    try {
      const monthlyRevenues = [
        parseFloat(formData.monthly_revenue_1) || 0,
        parseFloat(formData.monthly_revenue_2) || 0,
        parseFloat(formData.monthly_revenue_3) || 0,
        parseFloat(formData.monthly_revenue_4) || 0,
      ];

      // Build normalized working_data structure
      const workingData = {
        deal_terms: {
          state: formData.state,
          purchase_price: parseFloat(formData.purchase_price),
          factor_rate: parseFloat(formData.factor_rate) || 0,
          revenue_purchased: parseFloat(formData.revenue_purchased),
          origination_fee: parseFloat(formData.origination_fee) || 0,
          broker_fee_merchant: parseFloat(formData.broker_fee_merchant) || 0,
          broker_fee_funder: parseFloat(formData.broker_fee_funder) || 0,
          refi_amount: formData.has_refi ? (parseFloat(formData.refi_amount) || 0) : 0,
          third_party_payment: formData.has_third_party ? (parseFloat(formData.third_party_payment) || 0) : 0,
          payment_frequency: formData.payment_frequency,
          num_payment_weeks: parseInt(formData.num_payment_weeks),
          disbursement_date: formData.disbursement_date,
          first_payment_date: formData.first_payment_date,
          monthly_revenues: monthlyRevenues,
        },
        advanced_inputs: {
          is_ny_refi: formData.is_ny_refi,
          ny_prior_finance_charge: parseFloat(formData.ny_prior_finance_charge) || 0,
          ny_prior_percent_paid: parseFloat(formData.ny_prior_percent_paid) || 0,
          reconciliation_provision: 'Section 4',
          method_of_payment: 'ACH',
          fee_description: '',
          collateral_description: '',
          third_party_name: formData.has_third_party ? formData.third_party_name : '',
        },
      };

      const response = await axios.post(
        `${API_URL}/deals`,
        {
          merchant_id: selectedMerchantId,
          state: formData.state,
          funding_entity_id: selectedFundingEntityId,
          working_data: workingData,
        },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );

      navigate(`/deals/${response.data.id}`, { replace: true });
    } catch (err) {
      // Phase 4a: Use permission-aware error handler
      const processedError = handleAPIError(err);
      setError(err);
      console.error('Failed to create deal:', processedError);
    } finally {
      setLoading(false);
    }
  };

  // Styles
  const inputClass = {
    width: "100%", border: "1px solid #d1d5db", borderRadius: "6px",
    padding: "8px 10px", fontSize: "14px", boxSizing: "border-box", outline: "none"
  };
  const labelStyle = { display: "block", fontSize: "13px", fontWeight: "600", color: "#374151", marginBottom: "5px" };
  const sectionStyle = { backgroundColor: "white", borderRadius: "10px", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", padding: "24px", marginBottom: "20px", border: "1px solid #f0f0f0" };
  const sectionTitleStyle = { fontSize: "16px", fontWeight: "700", color: "#004D35", marginTop: 0, marginBottom: "18px", paddingBottom: "10px", borderBottom: `2px solid rgba(0,192,122,0.2)` };
  const gridStyle = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" };
  const checkRowStyle = { display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", marginBottom: "4px" };

  return (
    <div style={{ backgroundColor: "#f4faf7", minHeight: "100vh", fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
      <div style={{ maxWidth: "920px", margin: "0 auto", padding: "32px 16px" }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
          <h1 style={{ margin: 0, fontSize: '28px', fontWeight: '700', color: '#004D35' }}>Create New Deal</h1>
          <Link to="/deals" style={{ padding: '8px 16px', background: '#fff', border: '1px solid #ddd', borderRadius: '4px', textDecoration: 'none', color: '#333', fontSize: '14px', fontWeight: '500' }}>← Back</Link>
        </div>

        {/* Phase 4a: Use permission-aware error display */}
        <ErrorMessage error={error} onDismiss={() => setError(null)} />

        <form onSubmit={handleSubmit}>

          {/* Merchant Selection Section */}
          {!queryMerchantId && (
            <div style={sectionStyle}>
              <h2 style={sectionTitleStyle}>Select or Create Merchant</h2>
              <div style={gridStyle}>
                <div style={{ gridColumn: "span 2", position: 'relative' }}>
                  <label style={labelStyle}>Merchant *</label>
                  <input
                    type="text"
                    value={merchantSearch}
                    onChange={(e) => handleMerchantSearch(e.target.value)}
                    placeholder="Search by merchant name or EIN"
                    style={inputClass}
                    onFocus={() => merchantOptions.length > 0 && setShowMerchantDropdown(true)}
                  />
                  {showMerchantDropdown && merchantOptions.length > 0 && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #ddd', borderRadius: '4px', maxHeight: '200px', overflowY: 'auto', zIndex: 10, marginTop: '4px', width: '100%', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                      {merchantOptions.map((m) => (
                        <div
                          key={m.id}
                          style={{ padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid #eee', fontSize: '14px', transition: 'background 0.2s' }}
                          onClick={() => selectMerchant(m)}
                          onMouseEnter={(e) => e.target.style.background = '#f9fafb'}
                          onMouseLeave={(e) => e.target.style.background = 'white'}
                        >
                          {m.legal_name} ({m.ein})
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {selectedMerchant && (
                <div style={{ marginTop: "16px", padding: "16px", background: "#f9fafb", borderRadius: "6px", border: "1px solid #e5e7eb" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "12px" }}>
                    <div>
                      <div style={{ fontSize: "14px", fontWeight: "600", color: "#374151" }}>Selected Merchant</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedMerchantId(null);
                        setSelectedMerchant(null);
                        setSelectedMerchantAddress(null);
                        setMerchantSearch('');
                      }}
                      style={{ background: 'transparent', border: 'none', fontSize: '16px', cursor: 'pointer', padding: 0, color: '#666' }}
                    >
                      ✕
                    </button>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", fontSize: "13px" }}>
                    <div>
                      <div style={{ fontWeight: "600", color: "#4b5563", marginBottom: "2px" }}>Legal Name</div>
                      <div style={{ color: "#6b7280" }}>{selectedMerchant.legal_name}</div>
                    </div>
                    {selectedMerchant.dba_name && (
                      <div>
                        <div style={{ fontWeight: "600", color: "#4b5563", marginBottom: "2px" }}>DBA Name</div>
                        <div style={{ color: "#6b7280" }}>{selectedMerchant.dba_name}</div>
                      </div>
                    )}
                    {selectedMerchant.ein && (
                      <div>
                        <div style={{ fontWeight: "600", color: "#4b5563", marginBottom: "2px" }}>EIN</div>
                        <div style={{ color: "#6b7280" }}>{selectedMerchant.ein}</div>
                      </div>
                    )}
                    {selectedMerchant.entity_type && (
                      <div>
                        <div style={{ fontWeight: "600", color: "#4b5563", marginBottom: "2px" }}>Entity Type</div>
                        <div style={{ color: "#6b7280" }}>{selectedMerchant.entity_type}</div>
                      </div>
                    )}
                    {selectedMerchant.state_of_formation && (
                      <div>
                        <div style={{ fontWeight: "600", color: "#4b5563", marginBottom: "2px" }}>State of Formation</div>
                        <div style={{ color: "#6b7280" }}>{selectedMerchant.state_of_formation}</div>
                      </div>
                    )}
                    {selectedMerchantAddress && (
                      <div style={{ gridColumn: "span 2" }}>
                        <div style={{ fontWeight: "600", color: "#4b5563", marginBottom: "2px" }}>Primary Address</div>
                        <div style={{ color: "#6b7280" }}>
                          {selectedMerchantAddress.address_line_1}
                          {selectedMerchantAddress.address_line_2 && <><br />{selectedMerchantAddress.address_line_2}</>}
                          <br />{selectedMerchantAddress.city}, {selectedMerchantAddress.state} {selectedMerchantAddress.zip}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              <button
                type="button"
                onClick={() => setShowMerchantCreateModal(true)}
                style={{ marginTop: '16px', padding: '10px 16px', background: '#00C07A', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}
              >
                + Create New Merchant
              </button>
            </div>
          )}

          {selectedMerchant && (
            <div style={{ padding: "16px", background: "#f0fdf4", border: "1px solid #86efac", borderRadius: "6px", marginBottom: "20px" }}>
              <div style={{ fontSize: "12px", fontWeight: "600", color: "#374151", marginBottom: "10px" }}>Selected Merchant</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", fontSize: "13px" }}>
                <div>
                  <div style={{ fontWeight: "600", color: "#4b5563", marginBottom: "2px" }}>Legal Name</div>
                  <div style={{ color: "#6b7280" }}>{selectedMerchant.legal_name}</div>
                </div>
                {selectedMerchant.dba_name && (
                  <div>
                    <div style={{ fontWeight: "600", color: "#4b5563", marginBottom: "2px" }}>DBA</div>
                    <div style={{ color: "#6b7280" }}>{selectedMerchant.dba_name}</div>
                  </div>
                )}
                {selectedMerchant.ein && (
                  <div>
                    <div style={{ fontWeight: "600", color: "#4b5563", marginBottom: "2px" }}>EIN</div>
                    <div style={{ color: "#6b7280" }}>{selectedMerchant.ein}</div>
                  </div>
                )}
                {selectedMerchant.entity_type && (
                  <div>
                    <div style={{ fontWeight: "600", color: "#4b5563", marginBottom: "2px" }}>Entity Type</div>
                    <div style={{ color: "#6b7280" }}>{selectedMerchant.entity_type}</div>
                  </div>
                )}
                {selectedMerchant.state_of_formation && (
                  <div>
                    <div style={{ fontWeight: "600", color: "#4b5563", marginBottom: "2px" }}>State of Formation</div>
                    <div style={{ color: "#6b7280" }}>{selectedMerchant.state_of_formation}</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Funding Entity Section */}
          <div style={sectionStyle}>
            <h2 style={sectionTitleStyle}>Funding Entity</h2>
            <div style={gridStyle}>
              <div>
                <label style={labelStyle}>Select Funding Entity</label>
                <select
                  value={selectedFundingEntityId || ''}
                  onChange={handleFundingEntityChange}
                  style={inputClass}
                >
                  <option value="">Select funding entity</option>
                  {fundingEntities.map((entity) => (
                    <option key={entity.id} value={entity.id}>
                      {entity.display_name || entity.legal_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {selectedFundingEntity && (
              <div style={{ marginTop: '16px', padding: '12px 16px', background: '#f3f4f6', borderRadius: '6px', border: '1px solid #e5e7eb', fontSize: '13px' }}>
                <div style={{ fontWeight: '600', color: '#4b5563', marginBottom: '2px' }}>Legal Name</div>
                <div style={{ color: '#6b7280', marginBottom: selectedFundingEntity.display_name ? '8px' : '0' }}>{selectedFundingEntity.legal_name}</div>
                {selectedFundingEntity.display_name && (
                  <>
                    <div style={{ fontWeight: '600', color: '#4b5563', marginBottom: '2px' }}>Display Name</div>
                    <div style={{ color: '#6b7280' }}>{selectedFundingEntity.display_name}</div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Deal Structure Section */}
          <div style={sectionStyle}>
            <h2 style={sectionTitleStyle}>Deal Structure</h2>
            <div style={gridStyle}>
              <div>
                <label style={labelStyle}>State *</label>
                {selectedMerchant ? (
                  <>
                    <select
                      name="state"
                      value={formData.state}
                      onChange={handleFormChange}
                      style={inputClass}
                    >
                      {ALL_US_STATES.map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                    {selectedMerchant?.state_of_formation && (
                      <div style={{ marginTop: "4px", fontSize: "12px", color: "#00a050", fontWeight: "500" }}>
                        Default from merchant • Editable
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{...inputClass, background: '#f3f4f6', color: '#9ca3af', display: 'flex', alignItems: 'center', minHeight: '40px'}}>
                    Select a merchant first
                  </div>
                )}
              </div>
              <div>
                <label style={labelStyle}>Payment Frequency *</label>
                <select
                  name="payment_frequency"
                  value={formData.payment_frequency}
                  onChange={handleFormChange}
                  style={inputClass}
                >
                  <option value="daily">Daily (M-F)</option>
                  <option value="weekly">Weekly</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Purchase Price *</label>
                <CurrencyInput name="purchase_price" value={formData.purchase_price} onChange={handleFormChange} />
              </div>
              <div>
                <label style={labelStyle}>Factor Rate *</label>
                <input
                  style={inputClass}
                  name="factor_rate"
                  value={formData.factor_rate}
                  onChange={handleFormChange}
                  type="number"
                  step="0.001"
                  min="1"
                  placeholder="e.g., 1.32"
                />
              </div>
              <div>
                <label style={labelStyle}>Revenue Purchased *</label>
                <CurrencyInput
                  name="revenue_purchased"
                  value={formData.revenue_purchased}
                  onChange={handleFormChange}
                  placeholder="Auto-calculated from factor rate"
                  disabled={!!(formData.purchase_price && formData.factor_rate)}
                />
              </div>
              <div>
                <label style={labelStyle}>Origination Fee</label>
                <CurrencyInput name="origination_fee" value={formData.origination_fee} onChange={handleFormChange} />
              </div>
              <div>
                <label style={labelStyle}>Broker Fee (Merchant)</label>
                <CurrencyInput name="broker_fee_merchant" value={formData.broker_fee_merchant} onChange={handleFormChange} />
              </div>
              <div>
                <label style={labelStyle}>Broker Fee (Funder)</label>
                <CurrencyInput name="broker_fee_funder" value={formData.broker_fee_funder} onChange={handleFormChange} />
              </div>

              {/* Refi Section */}
              <div style={{ gridColumn: "span 2", borderTop: "1px solid #f3f4f6", paddingTop: "12px" }}>
                <label style={checkRowStyle}>
                  <input
                    type="checkbox"
                    name="has_refi"
                    checked={formData.has_refi}
                    onChange={handleFormChange}
                  />
                  <span style={{ fontSize: "13px", fontWeight: "600", color: "#374151" }}>This deal includes a refinance / prior MCA payoff</span>
                </label>
                {formData.has_refi && (
                  <div style={{ marginTop: "10px", maxWidth: "calc(50% - 8px)" }}>
                    <label style={labelStyle}>Refi / Prior MCA Payoff Amount</label>
                    <CurrencyInput name="refi_amount" value={formData.refi_amount} onChange={handleFormChange} />
                  </div>
                )}
              </div>

              {/* Third Party Section */}
              <div style={{ gridColumn: "span 2" }}>
                <label style={checkRowStyle}>
                  <input
                    type="checkbox"
                    name="has_third_party"
                    checked={formData.has_third_party}
                    onChange={handleFormChange}
                  />
                  <span style={{ fontSize: "13px", fontWeight: "600", color: "#374151" }}>This deal includes a third party disbursement</span>
                </label>
                {formData.has_third_party && (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginTop: "10px" }}>
                    <div>
                      <label style={labelStyle}>Third Party Payment Amount</label>
                      <CurrencyInput name="third_party_payment" value={formData.third_party_payment} onChange={handleFormChange} />
                    </div>
                    <div>
                      <label style={labelStyle}>Third Party Name</label>
                      <input
                        style={inputClass}
                        name="third_party_name"
                        value={formData.third_party_name}
                        onChange={handleFormChange}
                        placeholder="Name of third party"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Payment Schedule Section */}
          <div style={sectionStyle}>
            <h2 style={sectionTitleStyle}>Payment Schedule</h2>
            <div style={gridStyle}>
              <div>
                <label style={labelStyle}>Number of Payment Weeks *</label>
                <input
                  style={inputClass}
                  name="num_payment_weeks"
                  value={formData.num_payment_weeks}
                  onChange={handleFormChange}
                  type="number"
                />
                {paymentDays && (
                  <div style={{ marginTop: "6px", fontSize: "12px", color: "#004D35", fontWeight: "700", backgroundColor: "#f6fdf9", padding: "4px 10px", borderRadius: "4px", display: "inline-block" }}>
                    {paymentDays} payment {formData.payment_frequency === "daily" ? "days" : "payments"}
                  </div>
                )}
              </div>
              <div>
                <label style={labelStyle}>Disbursement Date *</label>
                <input
                  style={inputClass}
                  name="disbursement_date"
                  value={formData.disbursement_date}
                  onChange={handleFormChange}
                  type="date"
                />
              </div>
              <div>
                <label style={labelStyle}>First Payment Date *</label>
                <input
                  style={inputClass}
                  name="first_payment_date"
                  value={formData.first_payment_date}
                  onChange={handleFormChange}
                  type="date"
                />
              </div>
            </div>
          </div>

          {/* Monthly Revenue Section */}
          <div style={sectionStyle}>
            <h2 style={sectionTitleStyle}>Merchant Monthly Revenue (Last 4 Months)</h2>
            <div style={gridStyle}>
              {[1, 2, 3, 4].map(n => (
                <div key={n}>
                  <label style={labelStyle}>{monthNames[n - 1]}</label>
                  <CurrencyInput
                    name={`monthly_revenue_${n}`}
                    value={formData[`monthly_revenue_${n}`]}
                    onChange={handleFormChange}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* NY Specific Section */}
          {formData.state === "NY" && (
            <div style={sectionStyle}>
              <h2 style={sectionTitleStyle}>New York Specific</h2>
              <label style={checkRowStyle}>
                <input
                  type="checkbox"
                  name="is_ny_refi"
                  checked={formData.is_ny_refi}
                  onChange={handleFormChange}
                />
                <span style={{ fontSize: "13px", fontWeight: "600", color: "#374151" }}>Is this a NY Refinance? (triggers double-dipping disclosure)</span>
              </label>
              {formData.is_ny_refi && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginTop: "12px" }}>
                  <div>
                    <label style={labelStyle}>Finance Charge on Prior Funding</label>
                    <CurrencyInput
                      name="ny_prior_finance_charge"
                      value={formData.ny_prior_finance_charge}
                      onChange={handleFormChange}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>% of Prior Funding Paid Off</label>
                    <div style={{ position: "relative" }}>
                      <input
                        style={{ ...inputClass, paddingRight: "32px" }}
                        name="ny_prior_percent_paid"
                        value={formData.ny_prior_percent_paid}
                        onChange={handleFormChange}
                        type="number"
                        min="0"
                        max="100"
                        step="1"
                      />
                      <span style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)", color: "#6b7280", fontSize: "14px" }}>%</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Submit Button */}
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '30px', paddingBottom: '40px' }}>
            <button
              type="submit"
              disabled={loading}
              style={{
                backgroundColor: "#00C07A",
                color: "white",
                border: "none",
                borderRadius: "8px",
                padding: "12px 28px",
                fontSize: "15px",
                fontWeight: "600",
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.7 : 1
              }}
            >
              {loading ? 'Creating...' : 'Create Deal'}
            </button>
            <Link
              to="/deals"
              style={{
                backgroundColor: "#6c757d",
                color: "white",
                border: "none",
                borderRadius: "8px",
                padding: "12px 28px",
                fontSize: "15px",
                fontWeight: "600",
                textDecoration: "none",
                textAlign: "center"
              }}
            >
              Cancel
            </Link>
          </div>
        </form>

        {/* Merchant Creation Modal */}
        {showMerchantCreateModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: 'white',
              borderRadius: '10px',
              padding: '24px',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
            }}>
              <h2 style={{ marginTop: 0, marginBottom: '20px', fontSize: '18px', fontWeight: '700', color: '#004D35' }}>Create New Merchant</h2>
              <form onSubmit={handleCreateMerchant}>
                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>Legal Name *</label>
                  <input
                    type="text"
                    name="legal_name"
                    value={merchantCreateForm.legal_name}
                    onChange={handleMerchantCreateChange}
                    style={inputClass}
                    placeholder="Full legal business name"
                    required
                  />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>DBA Name</label>
                  <input
                    type="text"
                    name="dba_name"
                    value={merchantCreateForm.dba_name}
                    onChange={handleMerchantCreateChange}
                    style={inputClass}
                    placeholder="Doing business as"
                  />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>EIN *</label>
                  <input
                    type="text"
                    name="ein"
                    value={merchantCreateForm.ein}
                    onChange={handleMerchantCreateChange}
                    style={inputClass}
                    placeholder="XX-XXXXXXX or XXXXXXXXX"
                    required
                  />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>Entity Type</label>
                  <select
                    name="entity_type"
                    value={merchantCreateForm.entity_type}
                    onChange={handleMerchantCreateChange}
                    style={inputClass}
                  >
                    <option value="LLC">LLC</option>
                    <option value="Corp">Corp</option>
                    <option value="S-Corp">S-Corp</option>
                    <option value="Sole Prop">Sole Prop</option>
                    <option value="Partnership">Partnership</option>
                  </select>
                </div>
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={() => setShowMerchantCreateModal(false)}
                    style={{
                      padding: '10px 16px',
                      background: '#6c757d',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    style={{
                      padding: '10px 16px',
                      background: '#00C07A',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                  >
                    Create Merchant
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
